/* ============================================================
   ANTHEM VINYL — Stack theme
   theme.js — cart drawer, mobile menu, lightweight interactions
   ============================================================ */

(() => {
  'use strict';

  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const money = (cents) => {
    const value = (cents / 100).toFixed(2);
    return `€${value}`;
  };

  /* ---------- Cart drawer ---------- */
  const drawer  = $('[data-cart-drawer]');
  const overlay = $('[data-cart-overlay]');
  const openCart = () => {
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };
  const closeCart = () => {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  $$('[data-cart-open]').forEach(b => b.addEventListener('click', openCart));
  $$('[data-cart-close]').forEach(b => b.addEventListener('click', closeCart));
  overlay && overlay.addEventListener('click', closeCart);
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeCart(); });

  /* ---------- Cart API ---------- */
  const refreshCartUI = async () => {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await res.json();

      // count
      $$('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });

      // subtotal
      const sub = $('[data-cart-subtotal]');
      if (sub) sub.textContent = money(cart.total_price);

      // re-render line items
      const itemsRoot = $('[data-cart-items]');
      const footEl = $('[data-cart-foot]');
      if (!itemsRoot) return;

      if (cart.item_count === 0) {
        itemsRoot.innerHTML = `<p class="cart-drawer__empty">Your bag is empty.</p>`;
        if (footEl) footEl.hidden = true;
        return;
      }
      if (footEl) footEl.hidden = false;

      const lines = cart.items.map((item, i) => `
        <div class="cart-drawer__item" data-line-key="${item.key}" data-line="${i + 1}">
          ${item.image ? `<img src="${item.image}&width=200" alt="">` : `<div style="background:var(--vinyl-black);"></div>`}
          <div>
            <p class="cart-drawer__item-title"><a href="${item.url}" style="color:var(--bone-sleeve);">${item.product_title}</a></p>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-drawer__item-meta">${item.variant_title}</p>` : ''}
            <p class="cart-drawer__item-meta">${item.vendor || ''}</p>
            <div class="cart-drawer__qty">
              <button type="button" data-qty-decrease aria-label="Decrease quantity">−</button>
              <span data-qty>${item.quantity}</span>
              <button type="button" data-qty-increase aria-label="Increase quantity">+</button>
            </div>
          </div>
          <div>
            <p style="font-weight:700;">${money(item.final_line_price)}</p>
            <button type="button" data-line-remove style="margin-top:8px;font-size:11px;text-transform:uppercase;letter-spacing:0.1em;color:var(--bone-sleeve);text-decoration:underline;">Remove</button>
          </div>
        </div>
      `).join('');
      itemsRoot.innerHTML = lines;
    } catch (err) { console.error('Cart refresh failed', err); }
  };

  const changeLine = async (lineIndex, quantity) => {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: lineIndex, quantity })
      });
      await res.json();
      refreshCartUI();
    } catch (err) { console.error('Cart change failed', err); }
  };

  // Event delegation on the drawer
  drawer && drawer.addEventListener('click', e => {
    const lineEl = e.target.closest('[data-line]');
    if (!lineEl) return;
    const lineIndex = parseInt(lineEl.dataset.line, 10);
    const qtyEl = lineEl.querySelector('[data-qty]');
    const currentQty = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;

    if (e.target.matches('[data-qty-increase]')) changeLine(lineIndex, currentQty + 1);
    if (e.target.matches('[data-qty-decrease]')) changeLine(lineIndex, Math.max(0, currentQty - 1));
    if (e.target.matches('[data-line-remove]')) changeLine(lineIndex, 0);
  });

  /* ---------- Ajax add-to-cart from any [data-product-form] ---------- */
  $$('[data-product-form]').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('[type="submit"]');
      if (btn) { btn.disabled = true; btn.dataset.originalText = btn.textContent; btn.textContent = 'Adding…'; }
      try {
        const formData = new FormData(form);
        const res = await fetch('/cart/add.js', { method: 'POST', body: formData, headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error('Add to cart failed');
        await res.json();
        await refreshCartUI();
        openCart();
      } catch (err) {
        console.error(err);
        alert('Sorry — could not add to bag. Please try again.');
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = btn.dataset.originalText || 'Add to bag'; }
      }
    });
  });

  /* ---------- Mobile menu ---------- */
  const header = $('[data-header]');
  const menuToggle = $('[data-menu-toggle]');
  const nav = $('[data-nav]');

  const openNav = () => {
    if (window.innerWidth < 821) {
      nav.style.transform = 'translateX(-100%)';
      nav.classList.add('is-open');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          nav.style.transform = '';
        });
      });
    } else {
      nav.classList.add('is-open');
    }
    menuToggle.classList.add('is-open');
    menuToggle.setAttribute('aria-expanded', 'true');
    navOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  };
  const closeNav = () => {
    nav.classList.add('is-closing');
    setTimeout(() => {
      nav.classList.remove('is-open');
      nav.classList.remove('is-closing');
    }, 400);
    menuToggle.classList.remove('is-open');
    menuToggle.setAttribute('aria-expanded', 'false');
    navOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
  };

  // Create overlay
  const navOverlay = document.createElement('div');
  navOverlay.className = 'nav-overlay';
  document.body.appendChild(navOverlay);

  document.querySelectorAll('[data-menu-toggle]').forEach(btn => {
    btn.addEventListener('click', () => {
      nav.classList.contains('is-open') ? closeNav() : openNav();
    });
  });
  navOverlay.addEventListener('click', closeNav);
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeNav(); });

  /* ---------- Hide/show header on scroll (desktop only) ---------- */
  if (header && window.innerWidth >= 821) {
    let lastY = window.scrollY;
    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentY = window.scrollY;
          if (currentY < 80) {
            header.style.transform = 'translateY(0)';
          } else if (currentY > lastY) {
            header.style.transform = 'translateY(-100%)';
          } else {
            header.style.transform = 'translateY(0)';
          }
          lastY = currentY;
          ticking = false;
        });
        ticking = true;
      }
    });
  }


  /* ---------- Page transitions ---------- */
  document.documentElement.classList.add('js-ready');

  document.addEventListener('click', e => {
    const link = e.target.closest('a');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('mailto') || href.startsWith('tel')) return;
    if (link.target === '_blank') return;
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    document.documentElement.classList.add('is-leaving');
    setTimeout(() => { window.location.href = href; }, 150);
  });

  window.addEventListener('pageshow', () => {
    document.documentElement.classList.remove('is-leaving');
  });

})();
// Bio modal
document.addEventListener('click', function(e) {
  if (e.target.closest('[data-bio-open]')) {
    const modal = document.querySelector('[data-bio-modal]');
    if (modal) modal.classList.add('is-open');
  }
  if (e.target.closest('[data-bio-close]')) {
    const modal = document.querySelector('[data-bio-modal]');
    if (modal) modal.classList.remove('is-open');
  }
});
document.addEventListener('keydown', function(e) {
  if (e.key === 'Escape') {
    const modal = document.querySelector('[data-bio-modal]');
    if (modal) modal.classList.remove('is-open');
  }
});

// Product filter
document.addEventListener('DOMContentLoaded', function() {
  const firstBtn = document.querySelector('.product-filter__btn');
  if (firstBtn) firstBtn.classList.add('is-active');
});

document.addEventListener('click', function(e) {
  if (!e.target.matches('.product-filter__btn')) return;
  const btn = e.target;
  const filter = btn.dataset.filter;
  const grid = document.getElementById('product-grid-list');
  if (!grid) return;

  document.querySelectorAll('.product-filter__btn').forEach(b => b.classList.remove('is-active'));
  btn.classList.add('is-active');

  grid.querySelectorAll('li[data-type]').forEach(item => {
    if (filter === 'All' || item.dataset.type === filter) {
      item.style.display = '';
    } else {
      item.style.display = 'none';
    }
  });
});

// Gallery lightbox
(function() {
  let images = [];
  let current = 0;

  function open(index) {
    current = index;
    const modal = document.querySelector('[data-gallery-modal]');
    if (!modal) return;
    modal.querySelector('.gallery-modal__img').src = images[current];
    modal.classList.add('is-open');
  }

  function close() {
    const modal = document.querySelector('[data-gallery-modal]');
    if (modal) modal.classList.remove('is-open');
  }

  function prev() { open((current - 1 + images.length) % images.length); }
  function next() { open((current + 1) % images.length); }

  document.addEventListener('click', function(e) {
    if (e.target.closest('[data-gallery-open]')) {
      const el = e.target.closest('[data-gallery-open]');
      const dataEl = document.querySelector('[id^="gallery-data-"]');
      if (dataEl) images = JSON.parse(dataEl.textContent);
      open(parseInt(el.dataset.galleryOpen));
    }
    if (e.target.matches('[data-gallery-close]') || e.target.closest('[data-gallery-close]')) close();
    if (e.target.matches('[data-gallery-prev]')) prev();
    if (e.target.matches('[data-gallery-next]')) next();
  });

  document.addEventListener('keydown', function(e) {
    const modal = document.querySelector('[data-gallery-modal]');
    if (!modal || !modal.classList.contains('is-open')) return;
    if (e.key === 'Escape') close();
    if (e.key === 'ArrowLeft') prev();
    if (e.key === 'ArrowRight') next();
  });
})();

// Hero parallax
(function() {
  const hero = document.querySelector('.artist-hero__bg img, .artist-hero__bg video');
  if (!hero) return;
  function onScroll() {
    const scroll = window.scrollY;
    const heroHeight = hero.parentElement.parentElement.offsetHeight;
    if (scroll > heroHeight) return;
    hero.style.transform = 'translateY(' + (scroll * 0.3) + 'px)';
  }
  window.addEventListener('scroll', onScroll, { passive: true });
})();


// ── Product carousel ──────────────────────────────
document.querySelectorAll('[data-carousel]').forEach(carousel => {
  const track = carousel.querySelector('[data-carousel-track]');
  const thumbsEl = carousel.closest('.product__media')?.querySelector('[data-thumbnails]');
  if (!track) return;

  let current = 0;

  function goTo(index) {
    current = index;
    track.style.transform = `translateX(-${current * 100}%)`;
    if (thumbsEl) {
      thumbsEl.querySelectorAll('[data-thumb]').forEach(t => {
        t.classList.toggle('is-active', parseInt(t.dataset.thumb) === current);
      });
    }
  }

  if (thumbsEl) {
    thumbsEl.querySelectorAll('[data-thumb]').forEach(thumb => {
      thumb.addEventListener('click', () => goTo(parseInt(thumb.dataset.thumb)));
    });
  }

  const slides = track.querySelectorAll('[data-slide]');
  const total = slides.length;

  const prevBtn = carousel.querySelector('[data-carousel-prev]');
  const nextBtn = carousel.querySelector('[data-carousel-next]');

  if (prevBtn) prevBtn.addEventListener('click', () => goTo((current - 1 + total) % total));
  if (nextBtn) nextBtn.addEventListener('click', () => goTo((current + 1) % total));
});

// ── Product description expander ──────────────────
document.addEventListener('DOMContentLoaded', function() {
  setTimeout(function() {
    document.querySelectorAll('[data-description-toggle]').forEach(btn => {
      const inner = btn.previousElementSibling;
      if (!inner) return;

      if (inner.scrollHeight <= inner.clientHeight + 2) {
        btn.classList.add('is-hidden');
        return;
      }

      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        inner.classList.toggle('is-expanded', !expanded);
        btn.setAttribute('aria-expanded', !expanded);
        btn.textContent = expanded ? 'Read more' : 'Read less';
      });
    });
  }, 100);
});

// ── Homepage hero parallax ────────────────────────
(function() {
  const hero = document.querySelector('.hero');
  if (!hero) return;

  const monogram = hero.querySelector('.hero__monogram');
  const inner = hero.querySelector('.hero__inner');
  const isMobile = window.innerWidth < 821;

  function onScroll() {
    const scrolled = window.scrollY;
    const heroHeight = hero.offsetHeight;
    if (scrolled > heroHeight) return;

    if (monogram) {
      const speed = isMobile ? 0.2 : 0.18;
      monogram.style.transform = 'translateY(calc(-50% - ' + (scrolled * speed) + 'px))';
    }

    if (inner) {
      const fadeSpeed = isMobile ? 0.6 : 1.4;
      const opacity = Math.max(0, 1 - (scrolled / (heroHeight * fadeSpeed)));
      inner.style.opacity = opacity;
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
})();

// ── Contact page monogram parallax ────────────────
(function() {
  const monogram = document.querySelector('.contact__monogram');
  if (!monogram) return;

  function onScroll() {
    const scrolled = window.scrollY;
    monogram.style.transform = 'translateY(calc(-50% - ' + (scrolled * 0.15) + 'px))';
  }

  window.addEventListener('scroll', onScroll, { passive: true });
})();

  /* ---------- Cart note ---------- */
  document.addEventListener('input', function(e) {
    if (!e.target.matches('[data-cart-note]')) return;
    clearTimeout(e.target._noteTimer);
    e.target._noteTimer = setTimeout(function() {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: e.target.value })
      });
    }, 600);
  });
